export interface ParsedSemVer {
  major: string
  minor: string
  patch: string
  prerelease: string[]
  build: string[]
}

const semVerPattern =
  /^(0|[1-9][0-9]*)\.(0|[1-9][0-9]*)\.(0|[1-9][0-9]*)(?:-((?:0|[1-9][0-9]*|[0-9]*[A-Za-z-][0-9A-Za-z-]*)(?:\.(?:0|[1-9][0-9]*|[0-9]*[A-Za-z-][0-9A-Za-z-]*))*))?(?:\+([0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*))?$/

export function parseSemVer(value: string): ParsedSemVer {
  const match = semVerPattern.exec(value)
  if (match === null) {
    throw new Error(`无效的 SemVer 版本号：${value}`)
  }
  const major = match[1]
  const minor = match[2]
  const patch = match[3]
  if (major === undefined || minor === undefined || patch === undefined) {
    throw new Error(`无效的 SemVer 版本号：${value}`)
  }

  return {
    major,
    minor,
    patch,
    prerelease: match[4]?.split('.') ?? [],
    build: match[5]?.split('.') ?? []
  }
}

export function isValidSemVer(value: string): boolean {
  return semVerPattern.test(value)
}

function compareNumericIdentifiers(left: string, right: string): number {
  if (left.length !== right.length) return left.length < right.length ? -1 : 1
  if (left === right) return 0
  return left < right ? -1 : 1
}

function comparePrerelease(left: string[], right: string[]): number {
  if (left.length === 0 && right.length === 0) return 0
  if (left.length === 0) return 1
  if (right.length === 0) return -1

  const length = Math.max(left.length, right.length)
  for (let index = 0; index < length; index += 1) {
    const leftIdentifier = left[index]
    const rightIdentifier = right[index]
    if (leftIdentifier === undefined) return -1
    if (rightIdentifier === undefined) return 1
    if (leftIdentifier === rightIdentifier) continue

    const leftIsNumeric = /^[0-9]+$/.test(leftIdentifier)
    const rightIsNumeric = /^[0-9]+$/.test(rightIdentifier)
    if (leftIsNumeric && rightIsNumeric) {
      return compareNumericIdentifiers(leftIdentifier, rightIdentifier)
    }
    if (leftIsNumeric) return -1
    if (rightIsNumeric) return 1
    return leftIdentifier < rightIdentifier ? -1 : 1
  }
  return 0
}

export function compareSemVer(left: string, right: string): number {
  const leftVersion = parseSemVer(left)
  const rightVersion = parseSemVer(right)

  for (const key of ['major', 'minor', 'patch'] as const) {
    const comparison = compareNumericIdentifiers(
      leftVersion[key],
      rightVersion[key]
    )
    if (comparison !== 0) return comparison
  }

  return comparePrerelease(leftVersion.prerelease, rightVersion.prerelease)
}

export function isVersionAtLeast(
  currentVersion: string,
  minimumVersion: string
): boolean {
  return compareSemVer(currentVersion, minimumVersion) >= 0
}
